import React from 'react';
import { MonacoJsonEditor } from '../../../components/MonacoJsonEditor';
import { FieldsAndOperatorsPanel } from '../segments-tab/components/FieldsAndOperatorsPanel';
import type { AvailableFieldsResponse } from '../segments-tab/types';
import styles from './EditSegment.module.css';

interface SegmentDefinitionEditorProps {
  contentsJson: string;
  onContentsChange: (value: string) => void;
  onValidationChange: (isValid: boolean) => void;
  availableFields: AvailableFieldsResponse;
  isLoadingFields: boolean;
  fieldsError?: Error | null;
  isReadOnly: boolean;
  /** When true, validate "field" values against legacy names (fields_to_expose.name). When false, use jsonPath-derived names (conditionGroups format). */
  useLegacyFieldNames?: boolean;
  /** When true, omit the h2/p header (e.g. when used with EditorModeToggle). */
  hideHeader?: boolean;
  /** Explains why this editor was selected instead of the visual builder. */
  notice?: string;
}

export const SegmentDefinitionEditor: React.FC<SegmentDefinitionEditorProps> = ({
  contentsJson,
  onContentsChange,
  onValidationChange,
  availableFields,
  isLoadingFields,
  fieldsError,
  isReadOnly,
  useLegacyFieldNames = false,
  hideHeader = false,
  notice,
}) => {
  // Use legacy (f.name) or new (jsonPath-derived) field names so validation matches segment format
  const contactFields = useLegacyFieldNames
    ? (availableFields?.contactFieldsLegacy ?? availableFields?.contactFields ?? [])
    : (availableFields?.contactFields ?? []);
  const relatedFields = (availableFields?.relatedSchemas ?? []).flatMap(s =>
    useLegacyFieldNames ? (s.fieldsLegacy ?? s.fields ?? []) : (s.fields ?? []),
  );
  // Conditions can hop past contact-adjacent schemas, so validation spans every reachable schema
  const nestedFields = useLegacyFieldNames
    ? (availableFields?.nestedFieldsLegacy ?? availableFields?.nestedFields ?? [])
    : (availableFields?.nestedFields ?? []);
  const allValidFields = Array.from(
    new Set([...contactFields, ...relatedFields, ...nestedFields]),
  ).sort();

  return (
    <article className={styles.editor}>
      <header>
        {!hideHeader && (
          <>
            <h2>Segment Definition</h2>
            <p>Define your segment using JSON condition groups</p>
          </>
        )}
        {notice && <p className={styles.editorNotice}>{notice}</p>}
        <MonacoJsonEditor
          value={contentsJson}
          onChange={onContentsChange}
          height="400px"
          readOnly={isReadOnly}
          onValidationChange={onValidationChange}
          highlightPlaceholders
          validFields={allValidFields}
        />
      </header>

      <footer>
        <FieldsAndOperatorsPanel
          availableFields={availableFields}
          isLoadingFields={isLoadingFields}
          fieldsError={fieldsError}
          useLegacyFieldNames={useLegacyFieldNames}
        />
      </footer>
    </article>
  );
};
